import { BackdropProps, Box, BoxProps, Dialog, DialogProps } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { ReactNode, useRef } from 'react';

import { Font } from '@/components/shared/components/Font';
import { IconButton } from '@/components/shared/components/IconButton';
import { LoadingFill } from '@/components/shared/components/LoadingFill';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

export interface NemuruDialogProps {
  opened: boolean;
  setOpened: (opened) => void;
  children: NonNullable<ReactNode>;
  buttons?: ReactNode;
  showClose?: boolean;
  avoidDefaultClose?: boolean;
  removeDefaultButton?: boolean;
  hideButtonsWrapper?: boolean;
  onClose?: DialogProps['onClose'];
  loading?: boolean;
  dialogProps?: Partial<DialogProps>;
  boxProps?: Partial<BoxProps>;
  paperProps?: React.CSSProperties;
  backdropProps?: Partial<BackdropProps>;
  titleTextElement?: JSX.Element;
}

export const NemuruDialog = ({
  opened,
  setOpened,
  children,
  buttons = <></>,
  showClose = false,
  avoidDefaultClose = false,
  removeDefaultButton = false,
  hideButtonsWrapper = false,
  onClose,
  titleTextElement,
  loading = false,
  dialogProps,
  boxProps,
  backdropProps,
  paperProps,
  ...props
}: NemuruDialogProps) => {
  const { md, sm } = useScreen();
  const { shadows } = useCustomTheme();

  const isMobile = !md;

  const dialogRef = useRef<HTMLDivElement>(null);

  const close = () => {
    if (!showClose && avoidDefaultClose) {
      const buttonWrapper = dialogRef.current?.querySelector('button-wrapper');
      if (buttonWrapper && !buttonWrapper.classList.contains('shake-anim')) {
        buttonWrapper.classList.add('shake-anim');
        setTimeout(() => {
          buttonWrapper.classList.remove('shake-anim');
        }, 500);
      }
    } else {
      setOpened(false);
    }
  };

  const classes = makeStyles({
    paper: {
      boxShadow: shadows[12],
      ...paperProps,
    },
  })();

  return (
    <Dialog
      ref={dialogRef}
      open={opened}
      onClose={onClose ? onClose : close}
      maxWidth={dialogProps?.maxWidth || 'sm'}
      fullWidth
      fullScreen={isMobile}
      classes={classes}
      {...dialogProps}
      {...props}
    >
      <Box padding={sm ? 6 : 3} {...boxProps}>
        {showClose && (
          <Box position="absolute" top={16} right={16}>
            <IconButton
              size="small"
              icon="close"
              onClick={(event) => (onClose ? onClose(event, 'escapeKeyDown') : close())}
            />
          </Box>
        )}
        <Box display={'flex'} flexDirection={'column'} justifyContent={'space-between'} width={'100%'}>
          <Box zIndex={0}>
            {titleTextElement && (
              <Box mb={4}>
                <Font variant="h3" color="title">
                  {titleTextElement}
                </Font>
              </Box>
            )}
            {children}
          </Box>
          {isMobile && <Box my={6} />}
          {!hideButtonsWrapper && (
            <NemuruDialogButtons>
              <>
                {!removeDefaultButton && !avoidDefaultClose && (
                  <StandardButton onClick={close}>
                    <Text tId={'CLOSE'} />
                  </StandardButton>
                )}
                {buttons}
              </>
            </NemuruDialogButtons>
          )}
        </Box>
      </Box>
      <LoadingFill open={loading} {...backdropProps} />
    </Dialog>
  );
};

interface NemuruDialogButtonsProps {
  children: ReactNode;
}

export const NemuruDialogButtons = ({ children }: NemuruDialogButtonsProps) => {
  const { md } = useScreen();
  const { background } = useCustomTheme();

  const isMobile = !md;

  const buttonWrapperStyle = {
    boxShadow: `0 -125px 25px -40px inset ${background}`,
    position: isMobile ? 'fixed' : 'sticky',
    bottom: '0',
    right: '15px',
    marginBottom: isMobile ? '0' : '-25px',
    paddingBottom: '25px',
    paddingInline: '15px',
    // marginLeft: '-15px',
    ...(background && { boxShadow: `0 -125px 25px -40px inset ${background}` }),
  } as React.CSSProperties;

  return (
    <Box
      className="button-wrapper"
      display={'inline-flex'}
      width={'100%'}
      pt={8}
      justifyContent="flex-end"
      gridGap={'16px'}
      style={buttonWrapperStyle}
    >
      {children}
    </Box>
  );
};
